import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { WhatsappLogo, GearSix, PlugsConnected, CircleNotch } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import SyncJobsPanel from "./SyncJobsPanel";

const fmt = (iso, withDay) => {
  if (!iso) return "never";
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return d.toLocaleString(undefined, { timeZone: "America/New_York", ...(withDay || !sameDay ? { weekday: "long" } : {}), hour: "numeric", minute: "2-digit" });
};

export function useSyncStatus(interval = 20000) {
  const [s, setS] = useState(null);
  const load = useCallback(async () => { try { const { data } = await api.get("/dailylogs/sync/status"); setS(data); } catch {} }, []);
  useEffect(() => { load(); const t = setInterval(load, interval); return () => clearInterval(t); }, [load, interval]);
  return [s, load];
}

export function SyncStatusWidget({ onOpenSettings, canBackfill = true }) {
  const [s, load] = useSyncStatus();
  const [busy, setBusy] = useState(false);
  if (!s) return null;
  const ok = s.status === "connected" && s.worker_online;
  const act = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(typeof msg === "function" ? msg(r) : msg); } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); }
    finally { setBusy(false); load(); window.dispatchEvent(new Event("dl-synced")); }
  };
  const reconnect = () => { onOpenSettings(); act(() => api.post("/dailylogs/reconnect"), "Reconnecting WhatsApp…"); };
  return (
    <div className={`rounded-xl border px-3 py-2 text-[11px] min-w-[230px] ${ok ? "border-emerald-200 bg-white" : "border-rose-200 bg-rose-50/60"}`} data-testid="dl-sync-widget">
      <div className={`flex items-center gap-1.5 font-bold text-xs ${ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="dl-sync-widget-status">
        <WhatsappLogo size={14} weight="fill" /> WhatsApp {ok ? "Connected" : "Disconnected"}
        {s.running && <CircleNotch size={12} className="animate-spin text-slate-400 ml-1" data-testid="dl-sync-widget-running" />}
      </div>
      {ok ? (
        <div className="text-slate-600 mt-0.5 leading-snug">
          <div data-testid="dl-sync-widget-last">Last Synced: <b className="text-slate-800">{fmt(s.last_sync_at)}</b></div>
          <div data-testid="dl-sync-widget-next">Next Sync: <b className="text-slate-800">{s.running ? "running now…" : fmt(s.next_sync_at)}</b></div>
          <div>Auto Refresh: Every {s.interval_min} Minutes</div>
        </div>
      ) : (
        <div className="text-slate-600 mt-0.5" data-testid="dl-sync-widget-last">Last Successful Sync: <b className="text-slate-800">{fmt(s.last_sync_at, true)}</b></div>
      )}
      <div className="flex gap-1.5 mt-1.5">
        {ok ? (
          <button onClick={onOpenSettings} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 font-semibold text-slate-600 hover:border-[#327de3] transition-colors" data-testid="dl-sync-open-settings"><GearSix size={11} /> WhatsApp Settings</button>
        ) : (
          <button onClick={reconnect} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-1 font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors" data-testid="dl-sync-reconnect"><PlugsConnected size={11} /> Reconnect WhatsApp</button>
        )}
      </div>
      {ok && <SyncJobsPanel canBackfill={canBackfill} />}
      {!ok && <div className="mt-1 text-[10px] font-semibold text-rose-700" data-testid="dl-disconnected-notice">WhatsApp disconnected — displaying saved data</div>}
    </div>
  );
}
